/*
 * Copyright (c) 2020 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import CommonState from "common/types/CommonState";
import { ScheduleState } from "common/types/ScheduleState";
import ShiftStatus from "pages/Schedule/types/ShiftStatus";
import TimeSlotType from "pages/Schedule/types/TimeSlotType";
import Shift from "pages/Schedule/types/Shift";
import { api } from "store/api/shiftsApi";
import {
    ClaimType,
    ShiftClaimsStatus,
} from "store/slices/shiftClaimsPolling";
import TimeSlot from "pages/Schedule/types/TimeSlot";

const initialState = {
    schedule: [],
    pendingShifts: [],
} as unknown as ScheduleState;

export const scheduleSlice = createSlice({
    initialState,
    name: "schedule",
    reducers: {
        setHasPendingShift: (state, action: PayloadAction<boolean>) => {
            state.hasPendingShift = action.payload;
        },
        setPendingShift: (state, action: PayloadAction<Shift>) => {
            state.pendingShifts = [...state.pendingShifts, action.payload];
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(api.endpoints.getSchedule.matchFulfilled, (state, { payload }) => {
            const shifts = payload.schedule
                .filter((entity) => entity.xtype === TimeSlotType.Shift)
                .map((entity) => entity.entity);
            return {
                ...state,
                schedule: payload.schedule,
                hasPendingShift: shifts.some((shift) => shift.status === ShiftStatus.Pending),
            };
        });
        builder.addMatcher(api.endpoints.unApplyShift.matchFulfilled, (state, { payload }) => {
            const scheduleIndex = state.schedule.findIndex(
                ({ entity }: any) => entity.shiftId === payload.shift_id
            );
            return {
                ...state,
                schedule: [
                    ...state.schedule.slice(0, scheduleIndex),
                    { ...state.schedule[scheduleIndex], status: ShiftStatus.Pending },
                    ...state.schedule.slice(scheduleIndex + 1),
                ],
                hasPendingShift: true,
            };
        });
        builder.addMatcher(
            api.endpoints.getShiftClaimsStatuses.matchFulfilled,
            (state, { payload }) => {
                if (payload.length > 0) {
                    payload.forEach((shiftClaim) => {
                        const { shiftId, status, claimType, error } = shiftClaim;
                        if (status === ShiftClaimsStatus.Pending) {
                            const scheduleIndex = state.schedule.findIndex(
                                (timeSlot: TimeSlot) =>
                                    (timeSlot.entity as Shift).shiftId === shiftId &&
                                    timeSlot.xtype === TimeSlotType.Shift
                            );
                            if (scheduleIndex !== -1) {
                                (state.schedule[scheduleIndex].entity as Shift).status =
                                    ShiftStatus.Pending;
                            }
                        } else if (status === ShiftClaimsStatus.Success) {
                            if (claimType === ClaimType.Unclaim) {
                                state.schedule = state.schedule.filter(
                                    (timeSlot: TimeSlot) =>
                                        (timeSlot.entity as Shift).shiftId !== shiftId ||
                                        timeSlot.xtype !== TimeSlotType.Shift
                                );
                            } else if (claimType === ClaimType.Claim) {
                                const pendingShiftIndex = state.pendingShifts.findIndex(
                                    (shift) => shift.shiftId === shiftId
                                );
                                if (pendingShiftIndex !== -1) {
                                    const claimedShift = {
                                        ...state.pendingShifts[pendingShiftIndex],
                                        status: ShiftStatus.Confirmed,
                                    };
                                    state.pendingShifts.splice(pendingShiftIndex, 1);
                                    state.schedule.push({
                                        entity: claimedShift,
                                        xtype: TimeSlotType.Shift,
                                    });
                                }
                            }
                        } else if (
                            status === ShiftClaimsStatus.Failure &&
                            claimType === ClaimType.Unclaim
                        ) {
                            const scheduleIndex = state.schedule.findIndex(
                                (timeSlot: TimeSlot) =>
                                    (timeSlot.entity as Shift).shiftId === shiftId &&
                                    timeSlot.xtype === TimeSlotType.Shift
                            );
                            if (scheduleIndex !== -1) {
                                (state.schedule[scheduleIndex].entity as Shift).status =
                                    ShiftStatus.Error;
                                (state.schedule[scheduleIndex].entity as Shift).error =
                                    error?.errorKey;
                            }
                        }
                    });
                }
            }
        );
    },
});

export const scheduleSelector = createSelector(
    (state: CommonState) => state.schedule,
    (schedule) => schedule.schedule
);

export const schedulePendingShiftsSelector = createSelector(
    (state: CommonState) => state.schedule,
    (schedule) => schedule.pendingShifts
);

export const scheduleHasPendingShiftSelector = createSelector(
    (state: CommonState) => state.schedule,
    (schedule) => schedule.hasPendingShift
);

export default scheduleSlice.reducer;
